import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { consumeInstantPaint } from "@/lib/prerender";

export const Hero = () => {
  const { t } = useTranslation();
  // Chargement initial prerendu : premier écran déjà peint par le HTML statique
  // → pas de fade-in rejoué (le sous-titre est l'élément LCP de la home mobile).
  const [instant] = useState(() => consumeInstantPaint("/"));
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="grain relative flex min-h-screen items-end overflow-hidden bg-[#111827]" data-testid="hero-section">
      <motion.div style={{ y: yImg }} className="absolute inset-0">
        <img
          src="/images/hero-1376.webp"
          srcSet="/images/hero-828.webp 828w, /images/hero-1376.webp 1376w"
          sizes="100vw"
          fetchPriority="high"
          decoding="async"
          alt="Villa contemporaine NovaImmo au bord du lagon, île Maurice"
          className="h-full w-full scale-110 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-[#111827]/35 to-[#111827]/25" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 lg:px-8 lg:pb-32">
        <motion.p
          initial={instant ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-xs font-semibold uppercase tracking-[0.32em] text-[#4DB8FF]"
        >
          {t("hero.kicker")}
        </motion.p>
        <motion.h1
          initial={instant ? false : { opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-4xl font-display text-4xl leading-[1.08] text-white sm:text-5xl lg:text-6xl"
          data-testid="hero-title"
        >
          {t("hero.title")}
        </motion.h1>
        <motion.p
          initial={instant ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/80 md:text-lg"
        >
          {t("hero.subtitle")}
        </motion.p>
        <motion.div
          initial={instant ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/approche"
            data-testid="hero-cta-methode"
            className="inline-flex items-center gap-2 rounded-xl shadow-sm bg-white px-7 py-3.5 text-sm font-semibold text-[#111827] transition-transform duration-300 hover:scale-105"
          >
            {t("hero.cta1")}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/j-ai-un-terrain"
            data-testid="hero-cta-terrain"
            className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:border-[#4DB8FF] hover:text-[#4DB8FF]"
          >
            {t("hero.cta2")}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-1 text-white/50 md:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ChevronDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
};
